/**
 * Núcleo (puro) do recorte estilo Instagram para imagens de post social.
 *
 * Modelo de transformação — resolução-independente (não depende do tamanho em px
 * do frame na tela), guardado por imagem:
 *   - scale: zoom do usuário (>= 1; 1 = imagem "preenchendo" o quadro, sem borda).
 *   - cx, cy: ponto da imagem (em coords normalizadas [0..1]) que fica no CENTRO do quadro.
 *
 * A partir de (aspect, scale, cx, cy) + dimensões naturais da imagem, derivamos o
 * retângulo-fonte visível — tanto para exibir (frame -> css) quanto para recortar (canvas).
 */
export interface CropTransform {
  scale: number;
  cx: number;
  cy: number;
}

export function defaultCropTransform(): CropTransform {
  return { scale: 1, cx: 0.5, cy: 0.5 };
}

export const CROP_MAX_ZOOM = 4; // zoom máximo (como o IG, limita ampliação)
const CROP_MAX_OUTPUT_EDGE = 1080; // maior lado do arquivo exportado (padrão IG)
const CROP_JPEG_QUALITY = 0.92;

function clamp(v: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, v));
}

/**
 * Retângulo-fonte (em px naturais) visível dentro do quadro, para um dado aspect
 * (largura/altura do quadro) e transformação. Já com clamp para o quadro nunca
 * sair da imagem (sem borda).
 */
export function computeSourceRect(
  naturalW: number,
  naturalH: number,
  aspect: number,
  t: CropTransform
): { sx: number; sy: number; sw: number; sh: number } {
  // Maior retângulo com a proporção do quadro que cabe na imagem (scale = 1 => "cover").
  const imageAspect = naturalW / naturalH;
  let baseW: number;
  let baseH: number;
  if (imageAspect >= aspect) {
    // imagem mais "larga" que o quadro -> altura limita
    baseH = naturalH;
    baseW = baseH * aspect;
  } else {
    baseW = naturalW;
    baseH = baseW / aspect;
  }
  const scale = clamp(t.scale, 1, CROP_MAX_ZOOM);
  const sw = baseW / scale;
  const sh = baseH / scale;
  // Centro desejado (em px), com clamp p/ o retângulo ficar dentro da imagem.
  const cxPx = clamp(t.cx, 0, 1) * naturalW;
  const cyPx = clamp(t.cy, 0, 1) * naturalH;
  const sx = clamp(cxPx - sw / 2, 0, naturalW - sw);
  const sy = clamp(cyPx - sh / 2, 0, naturalH - sh);
  return { sx, sy, sw, sh };
}

/** Reaplica os limites em (cx, cy, scale) para uma transformação/aspect — evita borda. */
export function clampTransform(
  naturalW: number,
  naturalH: number,
  aspect: number,
  t: CropTransform
): CropTransform {
  const scale = clamp(t.scale, 1, CROP_MAX_ZOOM);
  const rect = computeSourceRect(naturalW, naturalH, aspect, { ...t, scale });
  // Recalcula cx/cy a partir do retângulo já clampado (mantém consistência).
  const cx = (rect.sx + rect.sw / 2) / naturalW;
  const cy = (rect.sy + rect.sh / 2) / naturalH;
  return { scale, cx, cy };
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.decoding = 'async';
    img.src = url;
  });
}

/**
 * Recorta a imagem conforme (aspect, transform) e devolve um novo File JPEG,
 * já na proporção escolhida e limitado a CROP_MAX_OUTPUT_EDGE no maior lado.
 * O `submitCreate` sobe este arquivo (não o original).
 */
export async function cropImageToFile(
  file: File,
  aspect: number,
  transform: CropTransform
): Promise<File> {
  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    const naturalW = img.naturalWidth;
    const naturalH = img.naturalHeight;
    const { sx, sy, sw, sh } = computeSourceRect(naturalW, naturalH, aspect, transform);

    // Dimensões de saída: respeita a proporção e limita o maior lado.
    let outW = sw;
    let outH = sh;
    const longEdge = Math.max(outW, outH);
    if (longEdge > CROP_MAX_OUTPUT_EDGE) {
      const k = CROP_MAX_OUTPUT_EDGE / longEdge;
      outW = Math.round(outW * k);
      outH = Math.round(outH * k);
    } else {
      outW = Math.round(outW);
      outH = Math.round(outH);
    }

    const canvas = document.createElement('canvas');
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file; // fallback defensivo: sobe original
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, outW, outH);

    const blob: Blob | null = await new Promise((res) =>
      canvas.toBlob((b) => res(b), 'image/jpeg', CROP_JPEG_QUALITY)
    );
    if (!blob) return file;

    const baseName = file.name.replace(/\.[^.]+$/, '');
    return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg', lastModified: Date.now() });
  } catch {
    return file; // qualquer falha -> sobe o original (não bloqueia a postagem)
  } finally {
    URL.revokeObjectURL(url);
  }
}
